import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Skeleton } from 'antd';
import { RightCircleOutlined, UserOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../../layouts/Layout';
import { useSecureService } from '../../store/service';
import { useAuth } from '../../hooks/useAuth';
import styles from './Dashboard.module.scss';
import { hasRole, ROLES_PROJECT_ORG_ADMIN } from '../../constants/roles';

type ProjectMetrics = {
  total: number;
  needsReview: number;
  updated: number;
  approved: number;
};

const Dashboard: React.FC = () => {
  const { getEntryCount, getUserDashboard, getUserNotifications } = useSecureService();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    internal: { total: 0, needsReview: 0, updated: 0, approved: 0 } as ProjectMetrics,
    external: { total: 0, needsReview: 0, updated: 0, approved: 0 } as ProjectMetrics,
    notificationsCount: 0,
    organizationsCount: 0,
    usersCount: 0
  });

  useEffect(() => {
    loadDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const startDate = getStartDate('all');
      const isAdmin = !!user && hasRole(user.role, ...ROLES_PROJECT_ORG_ADMIN);

      const [countResult, userDashboardResult, notificationsResult] = await Promise.all([
        getEntryCount(undefined, startDate),
        isAdmin ? getUserDashboard(startDate) : Promise.resolve(undefined as any),
        getUserNotifications()
      ]);

      const count = countResult?.data;
      const notifications = notificationsResult?.data?.notifications || [];
      const unreadNotifications = Array.isArray(notifications)
        ? notifications.filter((n: any) => !n?.isRead).length
        : (user?.unreadNotificationCount || 0);

      setDashboardData({
        internal: {
          total: count?.totalInternalProjects ?? 0,
          needsReview: count?.totalInternalNeedsReviewEntries ?? 0,
          updated: count?.totalInternalUpdatedEntries ?? 0,
          approved: count?.totalInternalApprovedEntries ?? 0
        },
        external: {
          total: count?.totalExternalProjects ?? 0,
          needsReview: count?.totalExternalNeedsReviewEntries ?? 0,
          updated: count?.totalExternalUpdatedEntries ?? 0,
          approved: count?.totalExternalApprovedEntries ?? 0
        },
        notificationsCount: unreadNotifications,
        organizationsCount: isAdmin ? (userDashboardResult?.data?.organizationsCount || 0) : 0,
        usersCount: isAdmin ? (userDashboardResult?.data?.usersCount || 0) : 0
      });
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStartDate = (filter: string) => {
    const now = new Date();
    switch (filter) {
      case 'thisMonth':
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
      case 'thisYear':
        return new Date(now.getFullYear(), 0, 1).toISOString();
      case 'all':
        return undefined;
      default:
        return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    }
  };

  const handleSeeAllClick = () => {
    navigate('/results?type=notifications');
  };

  const handleSectionCardClick = (section: 'internal' | 'external', cardType: string) => {
    const basePath = section === 'internal' ? '/internal-projects' : '/external-projects';
    if (cardType === 'total') {
      navigate(basePath);
      return;
    }
    // Needs Review / Updated / Approved -> entry list with filters
    const startDate = getStartDate('all');
    const statusMap: Record<string, string> = {
      needsReview: 'NeedsReview',
      updated: 'Updated',
      approved: 'Approved'
    };
    const status = statusMap[cardType];
    if (status) {
      const params = new URLSearchParams({ type: 'entry', status });
      if (startDate) params.set('startDate', startDate);
      params.set('scope', section === 'internal' ? 'Internal' : 'External');
      navigate(`/results?${params.toString()}`);
      return;
    }
    navigate(basePath);
  };

  const buildProjectCards = (metrics: ProjectMetrics) => [
    {
      title: 'Total Projects',
      value: metrics.total,
      description: 'All created projects.',
      color: '#F6FFED',
      type: 'total',
      clickable: true
    },
    {
      title: 'Needs Review',
      value: metrics.needsReview,
      description: 'Entries recently modified or revised',
      color: '#F6FFED',
      type: 'needsReview',
      clickable: true
    },
    {
      title: 'Updated Entries',
      value: metrics.updated,
      description: 'Entries recently modified or revised',
      color: '#F6FFED',
      type: 'updated',
      clickable: true
    },
    {
      title: 'Approved Entries',
      value: metrics.approved,
      description: 'Entries that have been reviewed & approved',
      color: '#F6FFED',
      type: 'approved',
      clickable: true
    }
  ];

  const directoryCards = [
    {
      value: dashboardData.organizationsCount,
      description: (
        <>
          Total number of  <span className={styles.directoryTextAccent}>organizations</span>
        </>
      ),
      color: '#F6FFED',
      type: 'brands',
      clickable: false
    },
    {
      value: dashboardData.usersCount,
      description: (
        <>
          Total number of  <span className={styles.directoryTextAccent}>registered</span>
        </>
      ),
      color: '#F6FFED',
      type: 'users',
      clickable: false
    }
  ];

  const internalCards = buildProjectCards(dashboardData.internal);
  const externalCards = buildProjectCards(dashboardData.external);
  const isAdmin = !!user && hasRole(user.role, ...ROLES_PROJECT_ORG_ADMIN);
  return (
    <MainLayout title="Dashboard">
      <div className={styles.dashboardContainer}>
        {/* Top Banner */}
        <div className={styles.topBannerWrapper}>
          <Card styles={{ body: { padding: 0 } }} className={styles.topBanner}>
            <div className={styles.linesSvg}>
              <img src="/images/dashboard-line.svg" alt="Lines" />
            </div>
            <Row gutter={0}>
              <Col span={12}>
                <div className={styles.bannerLeft}>
                  <div className={styles.statusTag}>Notifications</div>
                  <div className={styles.statusNumber}>
                  {loading ? (
                    <Skeleton.Input active size="small" style={{ width: 48, minWidth: 48, verticalAlign: 'middle' }} />
                  ) : (
                    dashboardData.notificationsCount
                  )}
                </div>
                  <div className={styles.statusText}>Entries are waiting for your feedback</div>
                </div>
              </Col>
              <Col span={12} className={styles.bannerRightCol}>
                <div className={styles.bannerRight}>
                  <Button type="primary" className={styles.seeAllButton} onClick={handleSeeAllClick}>
                    See All
                  </Button>
                </div>
              </Col>
            </Row>
          </Card>
        </div>

        {/* Overview Section — Internal, External: her giriş / rol için aynı yerleşim */}
        <div className={styles.overviewSection}>
          <div className={styles.sectionBlock}>
            <h3 className={styles.sectionTitle}>Internal Projects</h3>
            <Row gutter={[16, 16]}>
              {internalCards.map((card, index) => (
                <Col xs={12} sm={12} lg={6} className={styles.dashboardCardCol} key={`internal-${index}`}>
                  <Card
                    styles={{ body: { padding: 0 } }}
                    className={`${styles.dataCard} ${card.clickable ? styles.clickable : ''}`}
                    onClick={card.clickable ? () => handleSectionCardClick('internal', card.type) : undefined}
                    data-color="glow"
                  >
                    {card.clickable && <RightCircleOutlined className={styles.cardArrowIcon} style={{ color: '#F6FFED' }} />}
                    <div className={styles.cardContent}>
                      <div className={styles.statusTag} style={{ color: card.color }}>
                        {card.title}
                      </div>
                      <div className={styles.statusNumber} style={{ color: card.color }}>
                        {loading ? (
                          <Skeleton.Input active size="small" className={styles.cardSkeleton} />
                        ) : (
                          card.value
                        )}
                      </div>
                      <div className={styles.statusText}>
                        {card.description}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          <div className={styles.sectionBlock}>
            <h3 className={styles.sectionTitle}>External Projects</h3>
            <Row gutter={[16, 16]}>
              {externalCards.map((card, index) => (
                <Col xs={12} sm={12} lg={6} className={styles.dashboardCardCol} key={`external-${index}`}>
                  <Card
                    styles={{ body: { padding: 0 } }}
                    className={`${styles.dataCard} ${styles.externalDataCard} ${card.clickable ? styles.clickable : ''}`}
                    onClick={card.clickable ? () => handleSectionCardClick('external', card.type) : undefined}
                    data-color="glow"
                  >
                    {card.clickable && <RightCircleOutlined className={styles.cardArrowIcon} style={{ color: '#F6FFED' }} />}
                    <div className={styles.cardContent}>
                      <div className={styles.statusTag} style={{ color: card.color }}>
                        {card.title}
                      </div>
                      <div className={styles.statusNumber} style={{ color: card.color }}>
                        {loading ? (
                          <Skeleton.Input active size="small" className={styles.cardSkeleton} />
                        ) : (
                          card.value
                        )}
                      </div>
                      <div className={styles.statusText}>
                        {card.description}
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          {isAdmin && (
            <div className={styles.sectionBlock}>
              <h3 className={styles.sectionTitle}>Directory</h3>
              <Row gutter={[16, 16]}>
                {directoryCards.map((card, index) => (
                  <Col xs={24} sm={12} lg={7} className={styles.dashboardCardColDirectory} key={`directory-${index}`}>
                    <Card
                      styles={{ body: { padding: 0 } }}
                      className={`${styles.dataCard} ${styles.directoryDataCard}`}
                      data-color="glow"
                    >
                      <div className={styles.cardContent}>
                        <div className={styles.directoryCardTopRow}>
                          <div className={styles.statusNumber} style={{ color: card.color }}>
                            {loading ? (
                              <Skeleton.Input active size="small" className={styles.cardSkeleton} />
                            ) : (
                              card.value
                            )}
                          </div>
                          <div className={styles.directoryCardIcons}>
                            <div className={`${styles.directoryIconCircle} ${styles.directoryIcon1}`}>
                              <UserOutlined style={{ fontSize: 18, color: '#F6FFED' }} />
                            </div>
                            <div className={`${styles.directoryIconCircle} ${styles.directoryIcon2}`}>
                              <UserOutlined style={{ fontSize: 18, color: '#F6FFED' }} />
                            </div>
                            <div className={`${styles.directoryIconCircle} ${styles.directoryIcon3}`}>
                              <UserOutlined style={{ fontSize: 18, color: '#F6FFED' }} />
                            </div>
                            <PlusOutlined className={styles.directoryPlusIcon} />
                          </div>
                        </div>
                        <div className={styles.statusText}>
                          {card.description}
                        </div>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard; 